import { Layers2, ListMusic, Search } from "lucide-react";
import { useMemo, useState } from "react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  Button,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusMessage,
} from "@ceedero/ui";

import { useCloudLibrary, useRemoveLibraryProject } from "@/cloud/hooks/use-cloud-pull";
import { AppPage } from "@/components/app-page";
import { useTranslation } from "@/localization/hooks/use-translation";
import { CatalogSummary } from "@/music-catalog/components/music-catalog-page/catalog-summary";
import {
  CatalogToolbar,
  type CatalogFilter,
} from "@/music-catalog/components/music-catalog-page/catalog-toolbar";
import { ProjectCard } from "@/music-catalog/components/music-catalog-page/project-card";
import { ProjectTracksAccordion } from "@/music-catalog/components/music-catalog-page/project-tracks-accordion";
import { useCatalogViewStore } from "@/music-catalog/store/use-catalog-view-store";
import {
  buildDuplicateGroups,
  collectCatalogTracks,
  collectDuplicateEntryIds,
  countDuplicatesByProject,
  searchProjects,
  selectLiveProjects,
  sortProjects,
  type CatalogSortOrder,
} from "@/music-catalog/utils/catalog-view";

import type { LibraryProject } from "@/cloud/types/types";

/**
 * The projects and tracks this account's devices have synced.
 *
 * Named for what the desktop calls it, because it is the same thing seen from
 * elsewhere: the desktop has a Music Catalog and no "library", and a second
 * word for one concept is two concepts to whoever reads both.
 *
 * This application never publishes a project and never opens one for editing. A
 * project leaves here as the bytes the desktop wrote, so the only things
 * offered are looking inside one, taking a copy, and removing the cloud's.
 */
export const MusicCatalogPage = () => {
  const { t } = useTranslation();
  const library = useCloudLibrary();
  const removeProject = useRemoveLibraryProject();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<CatalogFilter>("all");
  // Outlives the page, so coming back from a project lands on the same view.
  const view = useCatalogViewStore((state) => state.view);
  const setView = useCatalogViewStore((state) => state.setView);
  const [order, setOrder] = useState<CatalogSortOrder>("updated");
  const [projectToRemove, setProjectToRemove] = useState<LibraryProject | undefined>(undefined);

  const projects = useMemo(
    () => (library.data ? selectLiveProjects(library.data.projects) : []),
    [library.data],
  );
  // Tracks come from both halves: a project's tracklist syncs inside the
  // project, and only tracks belonging to no project travel as Catalog Tracks.
  // Reading the standalone half alone is why this page once showed nothing
  // beside a desktop counting hundreds.
  const tracks = useMemo(
    () => (library.data ? collectCatalogTracks(library.data) : []),
    [library.data],
  );
  const duplicateGroups = useMemo(() => buildDuplicateGroups(tracks), [tracks]);
  const duplicatesByProject = useMemo(
    () => countDuplicatesByProject(duplicateGroups),
    [duplicateGroups],
  );
  const duplicateEntryIds = useMemo(
    () => collectDuplicateEntryIds(duplicateGroups),
    [duplicateGroups],
  );

  // One list serves both views. They differ in how a project is drawn, not in
  // which projects there are, and two filtered lists would be two chances for
  // the counts above to disagree with the rows below.
  const visibleProjects = useMemo(() => {
    const filtered =
      filter === "duplicates"
        ? projects.filter((project) => duplicatesByProject.has(project.desktopProjectId))
        : projects;
    return sortProjects({ order, projects: searchProjects({ projects: filtered, query }) });
  }, [duplicatesByProject, filter, order, projects, query]);

  const isSearching = query.trim().length > 0;

  const confirmRemove = () => {
    if (projectToRemove) {
      removeProject.mutate(projectToRemove.desktopProjectId);
      setProjectToRemove(undefined);
    }
  };

  return (
    <AppPage
      width="full"
      header={<PageHeader title={t("catalog.title")} description={t("catalog.description")} />}
    >
      <div className="flex flex-col gap-6">
        {library.isPending && <Skeleton className="h-24 w-full" />}

        {/*
          A failed read must read as "could not load" and never as "you have
          nothing": these are the user's own projects, and an empty page would
          say they had lost them.
        */}
        {library.isError && <StatusMessage tone="error">{t("catalog.unavailable")}</StatusMessage>}

        {library.data && (
          <CatalogSummary
            duplicateGroupCount={duplicateGroups.length}
            projectCount={projects.length}
            storageLimitBytes={library.data.storageLimitBytes}
            storedBytes={library.data.storedBytes}
            trackCount={tracks.length}
          />
        )}

        {projects.length > 0 && (
          <CatalogToolbar
            duplicateProjectCount={duplicatesByProject.size}
            filter={filter}
            onFilterChange={setFilter}
            onOrderChange={setOrder}
            onQueryChange={setQuery}
            onViewChange={setView}
            order={order}
            projectCount={projects.length}
            query={query}
            view={view}
          />
        )}

        {removeProject.isError && (
          <StatusMessage tone="error">{t("catalog.removeFailed")}</StatusMessage>
        )}

        {/*
          Three kinds of nothing, and they need different sentences: a catalog
          that is genuinely empty, a search that matched none of it, and the
          duplicates filter finding none — which is good news rather than a
          failure, and must not be drawn as one.
        */}
        {library.data && projects.length === 0 && (
          <EmptyState
            icon={<ListMusic />}
            title={t("catalog.empty.title")}
            description={t("catalog.empty.description")}
          />
        )}

        {projects.length > 0 && visibleProjects.length === 0 && filter === "duplicates" && (
          <EmptyState
            icon={<Layers2 />}
            title={t("catalog.noDuplicates.title")}
            description={t("catalog.noDuplicates.description")}
          />
        )}

        {projects.length > 0 &&
          visibleProjects.length === 0 &&
          filter !== "duplicates" &&
          isSearching && (
            <EmptyState
              icon={<Search />}
              title={t("catalog.noMatches.title")}
              description={t("catalog.noMatches.description")}
            />
          )}

        {view === "covers" && visibleProjects.length > 0 && (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {visibleProjects.map((project) => (
              <li key={project.desktopProjectId}>
                <ProjectCard
                  duplicateCount={duplicatesByProject.get(project.desktopProjectId) ?? 0}
                  isRemoving={
                    removeProject.isPending && removeProject.variables === project.desktopProjectId
                  }
                  onRemove={setProjectToRemove}
                  project={project}
                />
              </li>
            ))}
          </ul>
        )}

        {/*
          Grouped by project rather than listed flat: a track's project is what
          makes it findable, and "Fade Away" appearing three times is only
          useful once you can see which albums it is on.
        */}
        {view === "tracks" && visibleProjects.length > 0 && (
          <ul className="flex flex-col gap-2">
            {visibleProjects.map((project) => (
              <li key={project.desktopProjectId}>
                <ProjectTracksAccordion
                  duplicateCount={duplicatesByProject.get(project.desktopProjectId) ?? 0}
                  duplicateEntryIds={duplicateEntryIds}
                  project={project}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/*
        Asked about because it is the cloud's only copy. The confirmation is the
        one place it can be said that the device that saved it keeps its own.
      */}
      <AlertDialog
        open={projectToRemove !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setProjectToRemove(undefined);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>{t("catalog.removeTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("catalog.removeDescription")}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="ghost">
                {t("account.cancel")}
              </Button>
            </AlertDialogCancel>
            <Button type="button" variant="destructive" onClick={confirmRemove}>
              {t("catalog.remove")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppPage>
  );
};
